import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useListItemwiseStockSummaryv2Query } from '../apis/stockManagement.api';
import { listItemMasterSearchQuery, getItemFinancialYearSummaryQuery } from '../apis/getQuery.api';
import { useDispatch, useSelector } from 'react-redux';
import { setDbDateRange } from '../../../redux/reducers/DashboardSlice';
import { setSelectedItem, clearSelectedItem, restoreLastSelectedItem } from '../../../redux/reducers/inventoryItemSelectionSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCodeTranslationQuery } from '../../../redux/apis/getCodeTranslation.api';
import dayjs from 'dayjs';
import { formatUnixTimestamp } from '../../../library';
import {
  StockItemWiseData,
  StockItemWiseTotals,
  FormattedStockItemWiseTotals,
  ItemMaster,
  ItemOption,
  SelectedItem,
  UseStockItemWiseReturn,
  DateRange,
  ColumnFilter,
  RowData,
  FilterValue,
} from '../types';
import { ItemFinancialYearSummaryInput, ItemFinancialYearSummaryItem, ListItemwiseStockSummaryv2QueryVariables } from '../types/graphql';
import {
  ProcessedItemMaster,
  ProcessedCodeTranslation,
} from '../types/graphql';

const INITIAL_PAGE = 0;
const ROWS_PER_PAGE = 25;
const INITIAL_ITEM_STATE: SelectedItem = {
  itemid: '',
  itemname: '',
  itemcode: '',
};

const createData = (
  date: string, 
  particular: string, 
  voucher: string, 
  qty: number, 
  costItem: number, 
  totalCost: number, 
  closingStock: number, 
  stockAmount: number, 
  remark: string, 
  invoiceid: string
): StockItemWiseData => ({
  date,
  particular,
  voucher,
  qty,
  costItem,
  totalCost,
  closingStock,
  stockAmount,
  remark,
  invoiceid,
});

const useStockItemWise = (itemDetails: SelectedItem | null = null, includeOpeningBalance: boolean = true, startTimeStamp?: string | number, endTimeStamp?: string | number): UseStockItemWiseReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [page, setPage] = useState<number>(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);
  const [rows, setRows] = useState<StockItemWiseData[]>([]);
  const [isItemOpen, setIsItemOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [items, setItems] = useState<ItemMaster[]>([]);
  const defaultItemsRef = useRef<ItemMaster[]>([]);
  const [currentOffset, setCurrentOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
  const [userClearedSelection, setUserClearedSelection] = useState<boolean>(false);
  const [isUserTyping, setIsUserTyping] = useState<boolean>(false);
  const [hasInitialAutoSelection, setHasInitialAutoSelection] = useState<boolean>(false);
  // Track input values separately to allow free editing
  const [inputValueCode, setInputValueCode] = useState<string>('');
  const [inputValueName, setInputValueName] = useState<string>('');
  const [isEditingCode, setIsEditingCode] = useState<boolean>(false);
  const [isEditingName, setIsEditingName] = useState<boolean>(false);

  const INITIAL_TOTAL_STATE: StockItemWiseTotals = {
    totalOp: 0,
    totalPurchase: 0,
    totalClosingStock: 0,
    totalOpQty: 0,
    totalPurchaseQty: 0,
    totalClosingStockQty: 0,
  };

  const [totals, setTotals] = useState<StockItemWiseTotals>(INITIAL_TOTAL_STATE);
  
  // Cursor-based pagination state - store cursors per page
  const [pageCursors, setPageCursors] = useState<Record<number, { timestamp: number; transactionId: number }>>({
    0: { timestamp: 0, transactionId: 0 } // First page always starts with 0
  });
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [financialYearSummary, setFinancialYearSummary] = useState<ItemFinancialYearSummaryItem | null>(null);
  
  // Running balance tracking for FIFO/LIFO lot display
  const [runningLots, setRunningLots] = useState<Array<{ qty: number; rate: number; label: string }>>([]);
  const [salesDebt, setSalesDebt] = useState<number>(0);
  const currentLotsRef = useRef<Array<{ qty: number; rate: number; label: string }>>([]);
  const currentDebtRef = useRef<number>(0);

  // Helper function to reset lots
  const resetLots = useCallback(() => {
    setRunningLots([]);
    setSalesDebt(0);
    currentLotsRef.current = [];
    currentDebtRef.current = 0;
  }, []);

  const { report } = useSelector((state: any) => state?.DashboardDataReducer) || {};
  const reduxSelectedItem = useSelector((state: any) => state?.inventoryItemSelectionReducer?.selectedItem) || INITIAL_ITEM_STATE;
  
  const selectedItem = itemDetails || reduxSelectedItem;
  const financialYear = useSelector((state: any) => state?.userPrefrenceandReducer.financialYear);

  const { data: unitUQC, isLoading: idUqcLoading, isError: isUqcError } = useGetCodeTranslationQuery({ codetype: 'UNITS' });




  const unitMap = useMemo(() => {
    if (!unitUQC) return {};
    return unitUQC.reduce((acc: Record<string, string>, u: ProcessedCodeTranslation) => {
      const id = u.id;
      const codetext = u.codetext;
      if (id && codetext) {
        acc[id.toString()] = codetext;
      }
      return acc;
    }, {});
  }, [unitUQC]);

  const options = useMemo(() => {
    const goodsItems = (items || []).filter(item => item.itemtype === 'GOODS');
    
    // Create unique item options by itemcode to avoid duplicates
    const uniqueItemsMap = new Map();
    goodsItems.forEach((item) => {
      const unit = unitMap[item.measurementcd] ?? '';
      const key = item.itemcode;
      if (!uniqueItemsMap.has(key)) {
        uniqueItemsMap.set(key, {
          id: item.itemid,
          label: item.itemname,
          value: item.itemid,
          itemcode: item.itemcode,
          measurementcd: item.measurementcd,
          unit: unit,
        });
      }
    });
    
    const itemOptions = Array.from(uniqueItemsMap.values());
    
    // Create separate options for item codes and names
    const uniqueItemCodes = [...new Set(goodsItems.map(item => item.itemcode).filter(Boolean))];
    const uniqueItemNames = [...new Set(goodsItems.map(item => item.itemname).filter(Boolean))];
    
    const itemCodeOptions = [
      { id: 'all-codes', label: 'All', value: '', itemcode: '', measurementcd: '', unit: '' },
      ...uniqueItemCodes.map((itemcode, idx) => ({
        id: `code-${idx}`,
        label: itemcode,
        value: '',
        itemcode: itemcode,
        measurementcd: '',
        unit: '',
      }))
    ];
    
    const itemNameOptions = [
      { id: 'all-names', label: 'All', value: '', itemname: '', measurementcd: '', unit: '' },
      ...uniqueItemNames.map((itemname, idx) => ({
        id: `name-${idx}`,
        label: itemname,
        value: '',
        itemname: itemname,
        measurementcd: '',
        unit: '',
      }))
    ];
    
    return {
      itemCodeOptions,
      itemNameOptions,
      itemOptions
    };
  }, [items, unitMap]);

  console.log({options});


  // Get cursor values for current page
  const currentPageCursors = pageCursors[page] || { timestamp: 0, transactionId: 0 };

  const STOCK_VARIABLES = useMemo<ListItemwiseStockSummaryv2QueryVariables>(
    () => {
      const startTime = startTimeStamp || report?.startTimeStamp || 0;
      const endTime = endTimeStamp || report?.endTimeStamp || 0;
      
      const variables: ListItemwiseStockSummaryv2QueryVariables = {
        input: {
          starttimestamp: Number(startTime) || 0,
          endtimestamp: Number(endTime) || 0,
          itemid: Number(selectedItem.itemid) || 0,
          limit: rowsPerPage,
          cursortimestamp: currentPageCursors.timestamp,
          cursortransactionid: currentPageCursors.transactionId,
          openingbalance: includeOpeningBalance ? 1 : 0,
        },
      };
      return variables;
    },
    [startTimeStamp, endTimeStamp, report?.startTimeStamp, report?.endTimeStamp, selectedItem.itemid, rowsPerPage, includeOpeningBalance, currentPageCursors.timestamp, currentPageCursors.transactionId]
  );




  const isvariablesValid = STOCK_VARIABLES.input.starttimestamp > 0 && 
                           STOCK_VARIABLES.input.endtimestamp > 0 && 
                           STOCK_VARIABLES.input.itemid > 0;
  

  const { data: rowsData, isLoading: isDataLoading, isFetching: isDataFetching, error: queryError, refetch } = useListItemwiseStockSummaryv2Query(STOCK_VARIABLES, {
    skip: !isvariablesValid,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  console.log('rowsData', rowsData);
  

  const isItemLoading = isItemOpen;

  const fetchItemMaster = useCallback(async (searchWord: string = '', offset: number = 0, isLoadMore: boolean = false): Promise<void> => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setIsItemOpen(true);
    }
    
    const SEARCH_VARIABLES = {
      word: searchWord,
      type: '',
      rowfrom: offset,
      limitno: 20,
    };

    try {
      const itemMaster = await listItemMasterSearchQuery({ variable: SEARCH_VARIABLES });
      const searchResults = itemMaster ?? [];
      
      if (searchWord === '') {
        if (offset === 0) {
          defaultItemsRef.current = searchResults;
          setItems(searchResults);
          setTotalItems(searchResults.length);
        } else if (isLoadMore) {
          const newItems = [...defaultItemsRef.current, ...searchResults];
          defaultItemsRef.current = newItems;
          setItems(newItems);
          setTotalItems(newItems.length);
        }
      } else {
        if (offset === 0) {
          setItems(searchResults);
          setTotalItems(searchResults.length);
        } else if (isLoadMore) {
          setItems(prevItems => [...prevItems, ...searchResults]);
          setTotalItems(prevTotal => prevTotal + searchResults.length);
        }
      }
      
      setHasMore(searchResults.length === 20);
      setCurrentOffset(offset + searchResults.length);
      
    } catch (error) {
      if (searchWord === '' && offset === 0) {
        defaultItemsRef.current = [];
        setItems([]);
        setTotalItems(0);
      }
      setHasMore(false);
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setIsItemOpen(false);
      }
    }
  }, []);

  const loadMoreItems = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchItemMaster(searchTerm, currentOffset, true);
    }
  }, [fetchItemMaster, searchTerm, currentOffset, loadingMore, hasMore]);

  const resetPagination = useCallback(() => {
    setCurrentOffset(0);
    setHasMore(true);
    setLoadingMore(false);
  }, []);

  useEffect(() => {
    if (searchTerm !== undefined) {
      const isTyping = Boolean(searchTerm && searchTerm.length > 0);
      setIsUserTyping(isTyping);
      
      if (isTyping && userClearedSelection) {
        setUserClearedSelection(false);
      }
      
      const debounceTimer = setTimeout(() => {
        if (searchTerm || (!searchTerm && userClearedSelection)) {
          resetPagination();
          fetchItemMaster(searchTerm, 0, false);
        }
        setIsUserTyping(false);
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [searchTerm, fetchItemMaster, userClearedSelection]);

  useEffect(() => {
    if (!hasInitialLoad && items.length === 0 && !searchTerm && !isItemOpen) {
      setHasInitialLoad(true);
      resetPagination();
      fetchItemMaster('', 0, false);
    }
  }, [hasInitialLoad, items.length, searchTerm, isItemOpen, fetchItemMaster, resetPagination]);

  useEffect(() => {
    if (hasInitialLoad && 
        items.length === 0 && 
        !searchTerm && 
        !isItemOpen && 
        !isUserTyping && 
        userClearedSelection &&
        defaultItemsRef.current.length > 0) {
      setItems(defaultItemsRef.current);
    }
  }, [hasInitialLoad, items.length, searchTerm, isItemOpen, isUserTyping, userClearedSelection]);

  useEffect(() => {
    if (!location.state && selectedItem.itemid === '' && !userClearedSelection) {
      dispatch(restoreLastSelectedItem());
    }
  }, [location.state, selectedItem.itemid, dispatch, userClearedSelection]);

  useEffect(() => {
    if (selectedItem.itemid && selectedItem.itemid !== '') {
    }
  }, [selectedItem.itemid]);

  useEffect(() => {
    if (selectedItem.itemid && items.length > 0) {
      const isItemInList = items.some(item => item.itemid === selectedItem.itemid);
      if (!isItemInList) {
        const selectedItemData = {
          itemid: selectedItem.itemid,
          itemname: selectedItem.itemname,
          itemcode: selectedItem.itemcode,
          itemtype: 'GOODS',
          measurementcd: '',
        };
        setItems(prevItems => [selectedItemData, ...prevItems]);
        defaultItemsRef.current = [selectedItemData, ...defaultItemsRef.current];
      }
    }
  }, [selectedItem.itemid, selectedItem.itemname, selectedItem.itemcode, items.length]);

  useEffect(() => {
    if (location.state) {
      dispatch(setSelectedItem({
        itemid: location.state.itemid,
        itemname: location.state.itemname,
        itemcode: location.state.itemcode,
        unit: location.state.unit,
      }));
    } else {
      const storedItemData = localStorage.getItem('selectedItemForNewTab');
      if (storedItemData) {
        try {
          const itemData = JSON.parse(storedItemData);
          dispatch(setSelectedItem({
            itemid: itemData.itemid,
            itemname: itemData.itemname,
            itemcode: itemData.itemcode,
            unit: itemData.unit,
          }));
          localStorage.removeItem('selectedItemForNewTab');
        } catch (error) {
          localStorage.removeItem('selectedItemForNewTab');
        }
      }
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    if (location.state && location.state.itemid && (!location.state.unit || location.state.unit === 'N/A') && unitMap && Object.keys(unitMap).length > 0) {
      const currentItem = items.find(item => item.itemid === location.state.itemid && item.itemtype === 'GOODS');
      if (currentItem && unitMap[currentItem.measurementcd]) {
        dispatch(setSelectedItem({
          ...location.state,
          unit: unitMap[currentItem.measurementcd]
        }));
      }
    }
  }, [location.state, unitMap, items, dispatch]);

  useEffect(() => {
    setTotals(INITIAL_TOTAL_STATE);
    setRows([]);
    if (!rowsData || !selectedItem?.itemid || !Array.isArray(rowsData)) return;
    
    // Process each row and track lots for remark generation
    // Use refs for immediate access to current lots for pagination continuity
    let currentLots = page === 0 ? [] : [...currentLotsRef.current];
    let currentDebt = page === 0 ? 0 : currentDebtRef.current;
    
    const formattedRows = rowsData.map((item, index) => {
      let particular = item.particular;
      if (particular.includes('Debit Note')) {
        const bracketMatch = particular.match(/Debit Note \((.*?)\)/);
        if (bracketMatch) {
          const transactionType = bracketMatch[1].split(':')[0].trim();
          particular = `Debit Note (${transactionType})`;
        } 
      } else if (particular.includes('Credit Note')) {
        const bracketMatch = particular.match(/Credit Note \((.*?)\)/);
        if (bracketMatch) {
          const transactionType = bracketMatch[1].split(':')[0].trim();
          particular = `Credit Note (${transactionType})`;
        }
      } else {
        particular = particular.split('(')[0].trim();
      }
      
      // Generate remark and update lots
      const remarkResult = generateFormattedRemark(item, selectedItem, currentLots, currentDebt);
      currentLots = remarkResult.updatedLots;
      currentDebt = remarkResult.updatedDebt;
      
      return createData(
        formatUnixTimestamp(item.date),
        particular,
        item.voucher_no,
        Number(item.qty),
        Number(item.cost_item),
        Number(item.total_cost),
        Number(item.closing_stock),
        Number(item.closing_stock_amount),
        remarkResult.remark,
        item.invoiceid
      );
    });



    // Use financial year summary for totals if available, otherwise calculate from transaction data
    let newTotals: StockItemWiseTotals;
    
    if (financialYearSummary) {
      // Use financial year summary data for accurate totals
      newTotals = {
        totalOp: financialYearSummary.openingamt || 0,
        totalOpQty: financialYearSummary.openingqty || 0,
        totalPurchase: financialYearSummary.purchaseamt || 0,
        totalPurchaseQty: financialYearSummary.purchaseqty || 0,
        totalClosingStock: financialYearSummary.closingamt || 0,
        totalClosingStockQty: financialYearSummary.closingqty || 0,
      };
    } else {
      // Fallback to calculating from transaction data
      newTotals = rowsData.reduce(
        (acc, item) => {
          const particular = item.particular || '';
          const isCreditNote = particular.includes('Credit Note');
          const isDebitNote = particular.includes('Debit Note');
          const isSalesReturn = particular.includes('Sales Return');
          const isPurchaseReturn = particular.includes('Purchase Return');
          
          const transactionAmount = Math.abs(item.cost_item * item.qty) || 0;
          
          return {
            totalOp: acc.totalOp + (particular === 'Opening Stock' ? item.closing_stock_amount || 0 : 0),
            totalOpQty: acc.totalOpQty + (particular === 'Opening Stock' ? item.closing_stock || 0 : 0),
            
            totalPurchase: acc.totalPurchase + 
              (particular === 'Purchase' ? transactionAmount : 0) +
              (isDebitNote && isPurchaseReturn ? -transactionAmount : 0),
            
            totalPurchaseQty: acc.totalPurchaseQty + 
              (particular === 'Purchase' ? Math.abs(item.qty || 0) : 0) +
              (isDebitNote && isPurchaseReturn ? -Math.abs(item.qty || 0) : 0),
            
            // For closing stock, use the last item's closing stock values
            // Backend handles opening balance filtering, so this will be correct
            totalClosingStock: rowsData[rowsData.length - 1]?.closing_stock_amount || 0,
            totalClosingStockQty: rowsData[rowsData.length - 1]?.closing_stock || 0,
          };
        },
        {
          totalOp: 0,
          totalPurchase: 0,
          totalClosingStock: 0,
          totalOpQty: 0,
          totalPurchaseQty: 0,
          totalClosingStockQty: 0,
        }
      );
    }
    
    setTotals(newTotals);
    setRows(formattedRows);
    
    // Update running lots for next page using refs for immediate access
    setRunningLots(currentLots);
    setSalesDebt(currentDebt);
    currentLotsRef.current = currentLots;
    currentDebtRef.current = currentDebt;
    
    // Extract cursor values and total count from the last item in the response for pagination
    const lastItem = rowsData[rowsData.length - 1];
    if (lastItem) {
      const nextTimestamp = lastItem.next_cursor_timestamp || 0;
      const nextTransactionId = lastItem.next_cursor_transactionid || 0;
      const totalRows = lastItem.total_count || 0;
      
      // Update total count
      setTotalCount(totalRows);
      
      // Store cursors for next page (current page + 1)
      setPageCursors(prev => ({
        ...prev,
        [page + 1]: {
          timestamp: nextTimestamp,
          transactionId: nextTransactionId
        }
      }));
      
      // Determine if there's a next page based on current position vs total count
      const currentPosition = (page + 1) * rowsPerPage;
      const hasMoreData = currentPosition < totalRows;
      
      // Enable next button if there's more data available
      // Cursors are required for pagination, but we should still show next if data exists
      setHasNextPage(hasMoreData);
    } else {
      setTotalCount(0);
      setHasNextPage(false);
    }
  }, [rowsData, selectedItem, page, financialYearSummary]);

  // Reset cursors when item changes
  useEffect(() => {
    setPageCursors({ 0: { timestamp: 0, transactionId: 0 } });
    setHasNextPage(true);
    setTotalCount(0);
    setPage(INITIAL_PAGE);
    // Reset running lots for new item
    resetLots();
  }, [selectedItem.itemid, resetLots]);

  // Reset cursors when date range changes
  useEffect(() => {
    setPageCursors({ 0: { timestamp: 0, transactionId: 0 } });
    setHasNextPage(true);
    setTotalCount(0);
    setPage(INITIAL_PAGE);
    // Reset running lots for new date range
    resetLots();
  }, [report?.range?.startTimeStamp, report?.range?.endTimeStamp, startTimeStamp, endTimeStamp, resetLots]);

  // Fetch financial year summary for totals
  const fetchFinancialYearSummary = useCallback(async () => {
    if (!selectedItem?.itemid || !financialYear?.financialyearid) return;
    
    try {
      const input: ItemFinancialYearSummaryInput = {
        itemid: Number(selectedItem.itemid),
        financialyearid: Number(financialYear.financialyearid)
      };
      
      const summaryData = await getItemFinancialYearSummaryQuery({ input });
      if (summaryData && summaryData.length > 0) {
        setFinancialYearSummary(summaryData[0]);
      }
    } catch (error) {
      // Error handling for financial year summary fetch
    }
  }, [selectedItem?.itemid, financialYear?.financialyearid]);

  // Fetch financial year summary when item or financial year changes
  useEffect(() => {
    fetchFinancialYearSummary();
  }, [fetchFinancialYearSummary]);

  const toNum = (v: any): number => {
    const n = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatINR = (n: any): string => Number(toNum(n)).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  
  const formatTotals = (totals: StockItemWiseTotals): FormattedStockItemWiseTotals => ({
    totalOp: formatINR(totals.totalOp),
    totalPurchase: formatINR(totals.totalPurchase),
    totalClosingStock: formatINR(totals.totalClosingStock),
    totalOpQty: formatINR(totals.totalOpQty),
    totalPurchaseQty: formatINR(totals.totalPurchaseQty),
    totalClosingStockQty: formatINR(totals.totalClosingStockQty),
  });

  const normalizeRate = (r: any): number => toNum(r);

  function pluralizeUnit(singular: string): string {
    if (!singular) return 'units';
    const raw = String(singular).trim();
    const u = raw.toLowerCase();
    const uncountables = ['kg', 'g', 'mg', 'l', 'ltr', 'ml', 'm', 'cm', 'mm', 'pcs', 'pc', 'dozen'];
    if (uncountables.includes(u)) return raw;
    if (u.endsWith('s')) return raw;
    if (u.endsWith('x')) return raw.slice(0, -1) + 'xes';
    if (u.endsWith('y') && !/[aeiou]y$/i.test(raw)) return raw.slice(0, -1) + 'ies';
    return raw + 's';
  }

  function buildMovementText(arr: Array<{ qty: number; rate: number }>, fmtUnits: (q: number) => string): string {
    if (!arr || !arr.length) return '';
    const grouped = arr.reduce((acc, { qty, rate }) => {
      const q = toNum(qty);
      const r = toNum(rate);
      if (!q) return acc;
      acc[r] = (acc[r] || 0) + q;
      return acc;
    }, {} as Record<number, number>);
    return Object.entries(grouped)
      .map(([rate, q]) => ({ rate: Number(rate), qty: q }))
      .sort((a, b) => a.rate - b.rate)
      .map(({ qty, rate }) => `${fmtUnits(qty)} @ ₹${formatINR(rate)}`)
      .join(' + ');
  }

  function generateFormattedRemark(
    item: any, 
    selectedItemArg: SelectedItem, 
    runningLotsRef: Array<{ qty: number; rate: number; label: string }>, 
    salesDebtRef: number
  ): { remark: string; updatedLots: Array<{ qty: number; rate: number; label: string }>; updatedDebt: number } {
    const particularRaw = item?.particular || '';
    const particular = particularRaw.split('(')[0].trim();
    const bracketContent = particularRaw.match(/\((.*?)\)/);
    const extractedVoucher = bracketContent ? bracketContent[1].split(':')[1]?.trim() : null;
    const qtyRaw = toNum(item?.qty);
    const costRateRaw = normalizeRate(item?.cost_item);
    const voucher_no = item?.voucher_no;

    const unitSingular = selectedItemArg?.unit || 'unit';
    const unitPlural = pluralizeUnit(unitSingular);
    const fmtUnits = (q: number): string => {
      const n = toNum(q);
      const label = n === 1 ? unitSingular : unitPlural;
      return `${n} ${label}`;
    };

    // Create local copies to work with
    let localRunningLots = [...runningLotsRef];
    let localSalesDebt = salesDebtRef;

    const addLot = (q: number, rate: number, label: string): void => {
      const qn = toNum(q);
      const rn = normalizeRate(rate);
      if (qn > 0) localRunningLots.push({ qty: qn, rate: rn, label });
    };

    const consumeFIFOWithReport = (q: number): { remainder: number; used: Array<{ qty: number; rate: number }> } => {
      let rem = toNum(q);
      const used: Array<{ qty: number; rate: number }> = [];
      while (rem > 0 && localRunningLots.length) {
        const lot = localRunningLots[0];
        const take = Math.min(lot.qty, rem);
        if (take > 0) used.push({ qty: take, rate: lot.rate });
        lot.qty -= take;
        rem -= take;
        if (lot.qty <= 0) localRunningLots.shift();
      }
      return { remainder: rem, used };
    };

    const removeLIFOWithReport = (q: number): { remainder: number; removed: Array<{ qty: number; rate: number }> } => {
      let rem = toNum(q);
      const removed: Array<{ qty: number; rate: number }> = [];
      while (rem > 0 && localRunningLots.length) {
        const lot = localRunningLots[localRunningLots.length - 1];
        const take = Math.min(lot.qty, rem);
        if (take > 0) removed.push({ qty: take, rate: lot.rate });
        lot.qty -= take;
        rem -= take;
        if (lot.qty <= 0) localRunningLots.pop();
      }
      return { remainder: rem, removed };
    };

    let movementLine = '';

    if (particular === 'Opening Stock') {
      addLot(qtyRaw, costRateRaw, 'Op');
    } else if (particular === 'Purchase') {
      let purchased = qtyRaw;
      if (localSalesDebt > 0) {
        const clear = Math.min(localSalesDebt, purchased);
        localSalesDebt -= clear;
        purchased -= clear;
      }
      addLot(purchased, costRateRaw, voucher_no);
    } else if (particular === 'Purchase Return' || particular === 'Debit Note') {
      const remAbs = Math.abs(qtyRaw);
      const { remainder, removed } = removeLIFOWithReport(remAbs);
      const removedText = buildMovementText(removed, fmtUnits);
      if (removedText) {
        const voucherInfo = extractedVoucher ? ` (${extractedVoucher})` : '';
        movementLine = `Returned from lots: ${removedText}${voucherInfo}`;
      }
      if (remainder > 0) localSalesDebt += remainder;
    } else if (particular === 'Sales') {
      const absQ = Math.abs(qtyRaw);
      const { remainder, used } = consumeFIFOWithReport(absQ);
      const usedText = buildMovementText(used, fmtUnits);
      if (usedText) movementLine = `Issued from lots: ${usedText}`;
      if (remainder > 0) localSalesDebt += remainder;
    } else if (particular === 'Sales Return' || particular === 'Credit Note') {
      let returned = qtyRaw;
      let cleared = 0;
      if (localSalesDebt > 0) {
        const clear = Math.min(localSalesDebt, returned);
        localSalesDebt -= clear;
        returned -= clear;
        cleared = clear;
      }
      if (returned > 0) {
        addLot(returned, costRateRaw, voucher_no);
        const voucherInfo = extractedVoucher ? ` (${extractedVoucher})` : '';
        movementLine = `Returned to stock: ${fmtUnits(returned)} @ ₹${formatINR(costRateRaw)}${voucherInfo}`;
      } else if (cleared > 0) {
        const voucherInfo = extractedVoucher ? ` (${extractedVoucher})` : '';
        movementLine = `Applied to negative stock: ${fmtUnits(cleared)}${voucherInfo}`;
      }
    }

    // Show individual lots instead of average
    const closingLots = localRunningLots.filter((l) => l.qty > 0);
    const closingQty = closingLots.reduce((s, l) => s + toNum(l.qty), 0);

    if (closingQty <= 0) {
      const base = localSalesDebt > 0 ? 'Negative stock → value 0' : 'No stock available → value 0';
      return { remark: base, updatedLots: localRunningLots, updatedDebt: localSalesDebt };
    }

    // Group lots by rate and format them
    const groupedByRate = closingLots.reduce((acc, { qty, rate }) => {
      const q = toNum(qty);
      const r = toNum(rate);
      if (!q) return acc;
      acc[r] = (acc[r] || 0) + q;
      return acc;
    }, {} as Record<number, number>);

    const parts = Object.entries(groupedByRate)
      .map(([rate, q]) => ({ rate: Number(rate), qty: q }))
      .sort((a, b) => a.rate - b.rate)
      .map(({ qty, rate }) => `${fmtUnits(qty)} @ ₹${formatINR(rate)}`);

    const remark = parts.join(' + ');

    return { remark, updatedLots: localRunningLots, updatedDebt: localSalesDebt };
  }

  const handleChangePage = useCallback((event: any, newPage: number): void => {
    if (newPage < page) {
      // Going to previous page - reset lots since we can't maintain state backwards
      resetLots();
    }
    setPage(newPage);
  }, [page, resetLots]);

  const handleChangeRowsPerPage = useCallback((event: any): void => {
    setRowsPerPage(+event.target.value);
    setPage(INITIAL_PAGE);
    // Reset cursors when changing page size
    setPageCursors({ 0: { timestamp: 0, transactionId: 0 } });
    setHasNextPage(true);
    setTotalCount(0);
    // Reset running lots when changing page size
    resetLots();
  }, [resetLots]);

  const handleItemSelection = useCallback(
    ({ itemname, itemid, itemcode, unit }: Partial<SelectedItem>): void => {
      dispatch(setSelectedItem({ itemname, itemid, itemcode, unit }));
      setSearchTerm('');
      setIsUserTyping(false);
      
      // Reset input values and editing states when item is selected
      if (itemid) {
        setInputValueCode(itemcode || '');
        setInputValueName(itemname || '');
        setIsEditingCode(false);
        setIsEditingName(false);
      } else {
        setInputValueCode('');
        setInputValueName('');
        setIsEditingCode(false);
        setIsEditingName(false);
      }
      
      if (!itemid) {
        setUserClearedSelection(true);
        setHasInitialAutoSelection(false);
        setItems(defaultItemsRef.current);
      } else {
        setUserClearedSelection(false);
        // Restore default items list when an item is selected
        // This ensures dropdown shows all items when opened again
        if (defaultItemsRef.current.length > 0) {
          setItems(defaultItemsRef.current);
          setTotalItems(defaultItemsRef.current.length);
          setCurrentOffset(defaultItemsRef.current.length);
        } else {
          // If default items not loaded, fetch them
          resetPagination();
          fetchItemMaster('', 0, false);
        }
      }
      
      if (itemid) {
        navigate(location.pathname, {
          state: { itemid, itemname, itemcode, unit },
        });
      } else {
        navigate(location.pathname, {
          state: null,
        });
      }
    },
    [dispatch, navigate, location.pathname, fetchItemMaster, resetPagination]
  );

  useEffect(() => {
    if (selectedItem.itemid && !selectedItem.unit && unitMap && Object.keys(unitMap).length > 0) {
      const currentItem = items.find(item => item.itemid === selectedItem.itemid && item.itemtype === 'GOODS');
      if (currentItem && unitMap[currentItem.measurementcd]) {
        dispatch(setSelectedItem({
          ...selectedItem,
          unit: unitMap[currentItem.measurementcd]
        }));
      }
    }
  }, [selectedItem.itemid, selectedItem.unit, unitMap, items, dispatch]);

  // Sync input values when selected item changes (only when not editing)
  useEffect(() => {
    if (!isEditingCode && selectedItem.itemid) {
      setInputValueCode(selectedItem.itemcode || '');
    } else if (!selectedItem.itemid && !isEditingCode) {
      setInputValueCode('');
    }
  }, [selectedItem.itemid, selectedItem.itemcode, isEditingCode]);

  useEffect(() => {
    if (!isEditingName && selectedItem.itemid) {
      setInputValueName(selectedItem.itemname || '');
    } else if (!selectedItem.itemid && !isEditingName) {
      setInputValueName('');
    }
  }, [selectedItem.itemid, selectedItem.itemname, isEditingName]);

  const handleSelectPeriod = useCallback(
    (range: DateRange): void => {
      dispatch(
        setDbDateRange({
          outerKey: 'report',
          innerKey: 'range',
          value: range,
        })
      );
    },
    [dispatch]
  );

  const getRowColor = (row: RowData): string => {
    const p = row?.original?.particular;
    if (p === 'Sales') return '#4CAF50';
    if (p === 'Purchase') return '#f44336';
    if (p?.includes('Credit Note') || p?.includes('Sales Return')) return '#2196F3';
    if (p?.includes('Debit Note') || p?.includes('Purchase Return')) return '#FF9800';
    if (p === 'Opening Stock') return '#000000';
    return '#334155';
  };

  const dateBetweenFn = (row: RowData, columnId: string, filterValue: FilterValue): boolean => {
    if (!filterValue || (!filterValue.from && !filterValue.to)) return true;

    const raw = row.getValue(columnId);
    if (!raw) return false;

    let d = dayjs(raw, ['DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
    if (!d.isValid()) d = dayjs(raw);
    if (!d.isValid()) return false;

    const toDayjs = (v: any) => {
      if (!v) return null;
      if (dayjs.isDayjs(v)) return v;
      if (v instanceof Date) return dayjs(v);
      if (typeof v === 'number') return dayjs.unix(v);
      return dayjs(v);
    };

    const fromD = toDayjs(filterValue.from);
    const toD = toDayjs(filterValue.to);

    const fromOk = !fromD || d.isSame(fromD, 'day') || d.isAfter(fromD, 'day');
    const toOk = !toD || d.isSame(toD, 'day') || d.isBefore(toD, 'day');

    return fromOk && toOk;
  };

  const containsText = (row: RowData, columnId: string, value: string): boolean => {
    const v = row.getValue(columnId);
    return String(v ?? '')
      .toLowerCase()
      .includes(String(value ?? '').toLowerCase());
  };

  // Helper function to restore default items
  const restoreDefaultItems = useCallback(() => {
    if (defaultItemsRef.current.length > 0) {
      setItems(defaultItemsRef.current);
      setTotalItems(defaultItemsRef.current.length);
      setCurrentOffset(defaultItemsRef.current.length);
      setHasMore(defaultItemsRef.current.length >= 20);
    }
  }, []);

  // Handler to ensure items are available when dropdown opens
  const handleDropdownOpen = useCallback(() => {
    // Always restore default items when dropdown opens and no search is active
    if (!searchTerm || searchTerm.trim() === '') {
      if (defaultItemsRef.current.length > 0) {
        restoreDefaultItems();
      } else {
        fetchItemMaster('', 0, false);
      }
    }
  }, [searchTerm, fetchItemMaster, restoreDefaultItems]);

  // Handler for when input is cleared
  const handleInputClear = useCallback(() => {
    setSearchTerm('');
    restoreDefaultItems();
  }, [restoreDefaultItems]);

  // Handler for code input change - allows free editing
  const handleCodeInputChange = useCallback((value: string, reason: string) => {
    setInputValueCode(value);
    
    if (reason === 'input') {
      // User is actively typing/editing
      setIsEditingCode(true);
      setSearchTerm(value || '');
      
      // If user clears the input (backspace to empty), restore default items
      if (!value || value.trim() === '') {
        restoreDefaultItems();
      }
      
      // If user starts editing and there's a selected item, allow free editing
      // Selection will be cleared only if user selects a different item or clears completely
    } else if (reason === 'clear' || reason === 'reset') {
      // Input was cleared
      setIsEditingCode(false);
      setInputValueCode('');
      handleInputClear();
      // Clear selection when input is cleared
      if (selectedItem.itemid) {
        handleItemSelection({});
      }
    }
  }, [selectedItem, handleInputClear, handleItemSelection, restoreDefaultItems]);

  // Handler for when code input loses focus
  const handleCodeInputBlur = useCallback(() => {
    if (isEditingCode) {
      const currentInput = inputValueCode.trim();
      
      if (selectedItem.itemid) {
        if (currentInput === selectedItem.itemcode) {
          setIsEditingCode(false);
          return;
        }
        setInputValueCode(selectedItem.itemcode || '');
        setIsEditingCode(false);
      } else {
        if (currentInput !== '') {
          setInputValueCode('');
          setSearchTerm('');
        }
        setIsEditingCode(false);
        // Restore default items after clearing invalid input
        restoreDefaultItems();
      }
    }
  }, [isEditingCode, selectedItem, inputValueCode, restoreDefaultItems]);

  // Handler for name input change - allows free editing
  const handleNameInputChange = useCallback((value: string, reason: string) => {
    setInputValueName(value);
    
    if (reason === 'input') {
      // User is actively typing/editing
      setIsEditingName(true);
      setSearchTerm(value || '');
      
      // If user clears the input (backspace to empty), restore default items
      if (!value || value.trim() === '') {
        restoreDefaultItems();
      }
      
      // If user starts editing and there's a selected item, allow free editing
      // Selection will be cleared only if user selects a different item or clears completely
    } else if (reason === 'clear' || reason === 'reset') {
      // Input was cleared
      setIsEditingName(false);
      setInputValueName('');
      handleInputClear();
      // Clear selection when input is cleared
      if (selectedItem.itemid) {
        handleItemSelection({});
      }
    }
  }, [selectedItem, handleInputClear, handleItemSelection, restoreDefaultItems]);

  // Handler for when name input loses focus
  const handleNameInputBlur = useCallback(() => {
    if (isEditingName) {
      const currentInput = inputValueName.trim();
      
      if (selectedItem.itemid) {
        if (currentInput === selectedItem.itemname) {
          setIsEditingName(false);
          return;
        }
        setInputValueName(selectedItem.itemname || '');
        setIsEditingName(false);
      } else {
        if (currentInput !== '') {
          setInputValueName('');
          setSearchTerm('');
        }
        setIsEditingName(false);
        // Restore default items after clearing invalid input
        restoreDefaultItems();
      }
    }
  }, [isEditingName, selectedItem, inputValueName, restoreDefaultItems]);

  return {
    page,
    rowsPerPage,
    rows,
    isItemOpen,
    isItemLoading,
    isDataLoading: isDataLoading || isDataFetching,
    options,
    selectedItem,
    totals,
    formattedTotals: formatTotals(totals),
    range: report?.range || { startTimeStamp: '', endTimeStamp: '' },
    financialYear,
    expandedRows,
    columnFilters,
    showSearch,
    handleChangePage,
    handleChangeRowsPerPage,
    handleItemSelection,
    handleSelectPeriod,
    fetchItemMaster,
    setSearchTerm,
    getRowColor,
    dateBetweenFn,
    containsText,
    setExpandedRows,
    setColumnFilters,
    setShowSearch,
    searchTerm,
    loadMoreItems,
    hasMore,
    loadingMore,
    totalItems,
    hasNextPage,
    totalCount,
    itemCodeOptions: options.itemCodeOptions,
    itemNameOptions: options.itemNameOptions,
    itemOptions: options.itemOptions,
    handleDropdownOpen,
    handleInputClear,
    handleCodeInputChange,
    handleNameInputChange,
    handleCodeInputBlur,
    handleNameInputBlur,
    inputValueCode,
    inputValueName,
    isEditingCode,
    isEditingName,
  };
};

export default useStockItemWise;